import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { Button, Table, Tag } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import ProTableForm, {
  FormTableRef,
} from '@mpc/common/src/components/ProTableForm';
import {
  type OptionItemVo,
  v2RubyMigrateProductsByIdEdit,
} from '#services/api';
import {
  IPCooperationProductItem,
  DATA_INTERCONNECT_SUPPORTED,
  DATA_INTERCONNECT_UNSUPPORTED,
} from '../utils';

// 产品选项为两级树（分组 + children），选中值在叶子节点，需递归查找
const findChildOptions = (options: any[], values: string[]) => {
  let result: any[] = [];
  options.forEach((item) => {
    if (values.includes(item.value)) {
      result.push(item);
    }
    if (item.children?.length) {
      result = result.concat(findChildOptions(item.children, values));
    }
  });
  return result;
};

// #50369 临时隐藏 IP 合作需求「产品信息」表格的「产品负责人」列。
// 仅隐藏该展示列，不改数据管道（product_managers 的带入/扁平化/回填保持原样）；
// 恢复方式：置为 false 即重新展示该列。
const HIDE_PRODUCT_MANAGERS_COLUMN = true;

interface ProductInfoTableProps {
  value?: IPCooperationProductItem[];
  onChange?: (value: IPCooperationProductItem[]) => void;
  /** 所属产品下拉选项（来自 useDemandOptions(['product'])，两级树，仅叶子可选） */
  productOptions?: any[];
  /** 发行区域选项（来自 v2IpResourceOptions.country_region_options，经统一 modelConf optionMap 下传），
   *  用于渲染时翻译 USA/RUSSIA 等扩展值 */
  countryRegionOptions?: OptionItemVo[];
  /** 产品上架平台选项（来自 v2IpResourceOptions.launch_platform_options，经统一 modelConf optionMap 下传），
   *  用于渲染时翻译平台 code→中文名（镜像 countryRegionOptions） */
  launchPlatformOptions?: OptionItemVo[];
  /** 新建一级单仅允许 1 个产品：隐藏「新增产品」按钮与「操作」列（单元格仍可编辑） */
  readonly?: boolean;
  /** 详情只读：隐藏新增/操作，且「所属产品」「联动所属区域」单元格改为纯展示 */
  view?: boolean;
  lockProduct?: boolean;
}

/** 父表单 watchChildFormValidate 在提交时调用的校验能力（镜像 OnlineDetailTable 范式） */
export interface ProductInfoTableRef {
  validateFields: () => Promise<unknown>;
}

const ProductInfoTable = forwardRef<ProductInfoTableRef, ProductInfoTableProps>(
  (
    {
      value,
      onChange,
      productOptions,
      readonly,
      view,
      lockProduct,
      countryRegionOptions,
      launchPlatformOptions,
    },
    ref
  ) => {
    const tableRef = useRef<FormTableRef>(null);

    // 发行区域 code→name 动态映射（来自父级 optionMap.country_region_options，Apollo 动态下发，
    // 含 USA/RUSSIA 等扩展值；映射构建镜像 IPResource 列表页）
    const countryRegionMap = useMemo(() => {
      const map: Record<string, string> = {};
      (countryRegionOptions || []).forEach((it) => {
        if (it?.value != null) {
          map[it.value] = it?.name ?? it.value;
        }
      });
      return map;
    }, [countryRegionOptions]);

    // 翻译发行区域 code 为中文：country_region_options 动态映射，未知 code 保留原值兜底
    const getRegionName = useMemoizedFn((code: string): string => {
      return countryRegionMap[code] ?? code;
    });

    // 上架平台 code→中文名动态映射（来自父级 optionMap.launch_platform_options，Apollo 动态下发，
    // 映射构建镜像 countryRegionMap；miss 时兜底原 code）
    const launchPlatformMap = useMemo(() => {
      const map: Record<string, string> = {};
      (launchPlatformOptions || []).forEach((it) => {
        if (it?.value != null) {
          map[it.value] = it?.name ?? it.value;
        }
      });
      return map;
    }, [launchPlatformOptions]);

    // 翻译上架平台 code 为中文（镜像 getRegionName，miss 兜底原 code）
    const getLaunchPlatformName = useMemoizedFn((code: string): string => {
      return launchPlatformMap[code] ?? code;
    });

    // 39696：发行区域/上架平台可编辑列的选项 = Apollo 全量枚举 ∪ 行内现有值。
    // 历史产品库可能存有旧枚举值（如 DH2 的 iOS/Android），不在当前选项域内；
    // 合并进 options 兜底展示（tag 显示原值），避免被静默丢弃；提交时由后端枚举校验拦截非法值
    const mergeRowValueOptions = useMemoizedFn(
      (
        enumOptions: OptionItemVo[] | undefined,
        rowValues: string[] | undefined
      ): { name: string; value: string }[] => {
        const base = (enumOptions || [])
          .filter((it) => it?.value != null)
          .map((it) => ({ name: it?.name ?? it?.value, value: it?.value }));
        const known = new Set(base.map((o) => o.value));
        const extra = (rowValues || [])
          .filter((v) => v != null && !known.has(v))
          .map((v) => ({ name: v, value: v }));
        return [...base, ...extra];
      }
    );

    // 暴露校验能力：委托内部 ProTableForm.form.validateFields()，使父表单提交时联动校验
    // 各行「所属产品」「联动所属区域」必填（镜像 OnlineDetailTable 范式）
    useImperativeHandle(
      ref,
      () => ({
        validateFields: () => {
          if (!tableRef.current?.form?.validateFields) {
            return Promise.resolve();
          }
          return tableRef.current.form.validateFields().catch((error: any) => {
            const errMessage = error?.errorFields?.[0]?.errors?.[0];
            return Promise.reject(errMessage);
          });
        },
      }),
      []
    );

    // 记录最新一次发起的产品详情请求 id，快速切换产品时丢弃过期响应（竞态守卫）
    const latestProductIdRef = useRef<number | undefined>(undefined);

    // 选择产品后通过 v2RubyMigrateProductsByIdEdit 拉取产品详情并写回当前行
    // 字段直接对齐 ProductEditShowVo（不做 format 层），不触碰需求侧自填字段（linked_release_region 等）
    const handleProductSelect = useMemoizedFn(
      async (productIdValue: string | number | undefined, index: number) => {
        if (
          productIdValue === undefined ||
          productIdValue === null ||
          productIdValue === ''
        )
          return;
        const productId = Number(productIdValue);
        if (Number.isNaN(productId)) return;
        latestProductIdRef.current = productId;
        const res = await v2RubyMigrateProductsByIdEdit({ id: productId });
        if (latestProductIdRef.current !== productId) return;
        const detail = res?.data;
        if (!detail) return;
        const currentList: IPCooperationProductItem[] =
          (tableRef.current?.form?.getFieldValue('list') as
            | IPCooperationProductItem[]
            | undefined) || [];
        const updatedList = currentList.map((row, i) =>
          i === index
            ? {
                ...row,
                id: detail.id,
                name: detail.name,
                product_managers: detail.product_managers,
                release_regions: detail.release_regions,
                // 发行区域选项（Apollo 动态配置）用于渲染时翻译 USA/RUSSIA 等扩展值
                release_region_options: detail.options?.release_regions,
                launch_platforms: detail.launch_platforms,
                data_interconnection: detail.data_interconnection,
                // 选品自动带入联动区域/上架平台（仅空值预填，保留用户编辑；FR-1/FR-2 20260812 变更）
                linked_release_region: detail.release_regions,
                linked_platform: detail.launch_platforms,
              }
            : row
        );
        tableRef.current?.form?.setFieldsValue({ list: updatedList });
        // setFieldsValue 仅更新 antd Form 内部状态，不会同步受控 value/dataSource
        // 必须显式触发 onChange 让父组件 formData.products 更新，
        // 否则表格 dataSource 仍是旧数据 → 所有 render 列显示 --，联动区域列 formItem 闭包过期 → 下拉框空
        onChange?.(updatedList);
      }
    );

    // 39696：产品发行区域/上架平台改为可编辑后，变更时自动移除对应联动列已选值中超出新范围的项
    //（联动列 options 读行内 record.release_regions / record.launch_platforms 当前值，formItem 按 render 时
    //  最新 record 求值，天然跟随编辑值，无需快照修正）
    const syncLinkedAfterProductFieldChange = useMemoizedFn(
      (
        regionField: 'release_regions' | 'launch_platforms',
        linkedField: 'linked_release_region' | 'linked_platform',
        nextValue: string[] | undefined,
        index: number
      ) => {
        const currentList: IPCooperationProductItem[] =
          (tableRef.current?.form?.getFieldValue('list') as
            | IPCooperationProductItem[]
            | undefined) || [];
        const nextValues = nextValue || [];
        const updatedList = currentList.map((row, i) =>
          i === index
            ? {
                ...row,
                [regionField]: nextValues,
                [linkedField]: (row[linkedField] || []).filter((v) =>
                  nextValues.includes(v)
                ),
              }
            : row
        );
        tableRef.current?.form?.setFieldsValue({ list: updatedList });
        // setFieldsValue 仅更新 antd Form 内部状态，不会同步受控 value/dataSource
        // 必须显式触发 onChange 让父组件 formData.products 更新（镜像 handleProductSelect 范式）
        onChange?.(updatedList);
      }
    );

    const formColumns = useMemo(() => {
      return ({
        remove,
      }: {
        add: any;
        remove: (i: number | number[]) => void;
      }) => {
        const columns = [
          {
            title: '所属产品',
            dataIndex: 'id',
            width: 280,
            required: !(view || lockProduct),
            // view 态（详情只读）纯文本展示产品：有 formItem 时 render 是死代码，且只读态 productOptions 未加载，
            // view select 无法按 id 反查选项导致显示 --（对齐 OnlineDetailTable 的 view 二选一范式）；
            // 详情只读渲染 identifier（name），identifier 缺失时回退 name（兼容旧数据/新建态）
            ...(view || lockProduct
              ? {
                  render: (_t: any, record: IPCooperationProductItem) => {
                    const name = record?.name ?? '--';
                    return record?.identifier
                      ? `${record.identifier}（${name}）`
                      : name;
                  },
                }
              : {
                  formItem: (_text: any, _record: any, index: number) => ({
                    componentClass: 'select',
                    elementProps: {
                      className: 'w-[256px]',
                      placeholder: '请选择',
                      options: productOptions,
                      showSearch: true,
                      // tree 选择模式下返回纯叶子 value，避免 onChange 拿到节点对象导致 select 显示 [object Object]
                      returnOnlyLeaf: true,
                      treeProps: {
                        isSelectable: (node: any) => !node?.children?.length,
                      },
                      onChange: (value: string | number) => {
                        handleProductSelect(value, index);
                      },
                    },
                  }),
                }),
          },
          // #50369 临时隐藏「产品负责人」列（HIDE_PRODUCT_MANAGERS_COLUMN=false 时恢复展示）
          ...(HIDE_PRODUCT_MANAGERS_COLUMN
            ? []
            : [
                {
                  title: '产品负责人',
                  dataIndex: 'product_managers',
                  width: 100,
                  render: (_t: any, record: IPCooperationProductItem) =>
                    record.product_managers
                      ?.map((m) => m.name)
                      .filter(Boolean)
                      .join('/') || '--',
                },
              ]),
          {
            title: '产品发行区域',
            dataIndex: 'release_regions',
            width: 280,
            required: !view,
            // 39696：发行区域改为可编辑（镜像「联动所属区域」列 select 多选范式）；
            // options 用全量枚举 countryRegionOptions（Apollo 动态下发），非产品库子集
            formItem: (
              _text: any,
              record: IPCooperationProductItem,
              index: number
            ) => ({
              view: view || lockProduct,
              componentClass: 'select',
              rules: [
                {
                  validator: (_rule: any, value: any) => {
                    // 发行区域必填：多选 value 为数组，空值可能是 undefined 或 []，
                    // 默认 required 规则对 [] 不生效（async-validator 无 type 时 [] 判非空），
                    // 须显式校验数组非空（镜像「联动所属区域」数组校验范式）
                    if (!Array.isArray(value) || !value.length) {
                      return Promise.reject(new Error('请选择产品发行区域'));
                    }
                    return Promise.resolve();
                  },
                },
              ],
              elementProps: {
                // 39696：选项=Apollo 全量枚举 ∪ 行内现有值（历史脏值兜底展示）；
                // 注意 formColumns useMemo 依赖须含 countryRegionOptions/launchPlatformOptions，
                // 否则 Apollo 异步数据到达后本闭包仍捕获初始空数组 → 下拉「暂无选项」
                options: mergeRowValueOptions(
                  countryRegionOptions,
                  record?.release_regions
                ),
                className: 'w-[256px]',
                multiple: true,
                placeholder: '请先选择产品',
                showSearch: true,
                // 产品未选（无 id）时锁定，仅展示带入值不可改（与联动列门控一致）
                disabled: !record?.id,
                // 变更时自动移除「联动所属区域」已选值中超出新范围的项（写回行数据）
                onChange: (value: string[]) => {
                  syncLinkedAfterProductFieldChange(
                    'release_regions',
                    'linked_release_region',
                    value,
                    index
                  );
                },
                showComponent: (value: any) => {
                  return (
                    value?.map((item: any) => getRegionName(item)).join('/') ||
                    '--'
                  );
                },
              },
            }),
          },
          {
            title: '联动所属区域',
            dataIndex: 'linked_release_region',
            width: 280,
            required: !view,
            formItem: (
              _text: any,
              record: IPCooperationProductItem,
              _index: number
            ) => ({
              view,
              componentClass: 'select',
              rules: [
                {
                  validator: (_rule: any, value: any) => {
                    // 联动所属区域必填：多选 value 为数组，空值可能是 undefined 或 []，
                    // 默认 required 规则对 [] 不生效（async-validator 无 type 时 [] 判非空），
                    // 须显式校验数组非空（镜像 products「请选择产品」/ $first_exposure_at 数组校验范式）
                    if (!Array.isArray(value) || !value.length) {
                      return Promise.reject(new Error('请选择联动所属区域'));
                    }
                    return Promise.resolve();
                  },
                },
              ],
              elementProps: {
                options: (record?.release_regions || []).map((r: string) => ({
                  name: getRegionName(r),
                  value: r,
                })),
                className: 'w-[256px]',
                multiple: true,
                placeholder: '请先选择产品',
                showSearch: true,
                // 二级单（productColumnLocked）：联动所属区域锁定，仅展示继承值不可改
                disabled: !record?.id,
                showComponent: (value: any) => {
                  return (
                    value?.map((item: any) => getRegionName(item)).join('/') ||
                    '--'
                  );
                },
              },
            }),
          },
          {
            title: '产品上架平台',
            dataIndex: 'launch_platforms',
            width: 280,
            required: !view,
            // 39696：上架平台改为可编辑（镜像「联动所属区域」列 select 多选范式）；
            // options 用全量枚举 launchPlatformOptions（Apollo 动态下发），非产品库子集
            formItem: (
              _text: any,
              record: IPCooperationProductItem,
              index: number
            ) => ({
              view: view || lockProduct,
              componentClass: 'select',
              rules: [
                {
                  validator: (_rule: any, value: any) => {
                    // 上架平台必填：多选 value 为数组，空值可能是 undefined 或 []，
                    // 默认 required 规则对 [] 不生效（async-validator 无 type 时 [] 判非空），
                    // 须显式校验数组非空（镜像「联动所属区域」数组校验范式）
                    if (!Array.isArray(value) || !value.length) {
                      return Promise.reject(new Error('请选择产品上架平台'));
                    }
                    return Promise.resolve();
                  },
                },
              ],
              elementProps: {
                // 39696：镜像发行区域列——枚举 ∪ 行内现有值（历史脏值如 iOS/Android 兜底展示）
                options: mergeRowValueOptions(
                  launchPlatformOptions,
                  record?.launch_platforms
                ),
                className: 'w-[256px]',
                multiple: true,
                placeholder: '请先选择产品',
                showSearch: true,
                // 产品未选（无 id）时锁定，仅展示带入值不可改（与联动列门控一致）
                disabled: !record?.id,
                // 变更时自动移除「联动上架平台」已选值中超出新范围的项（写回行数据）
                onChange: (value: string[]) => {
                  syncLinkedAfterProductFieldChange(
                    'launch_platforms',
                    'linked_platform',
                    value,
                    index
                  );
                },
                showComponent: (value: any) => {
                  return (
                    value
                      ?.map((item: any) => getLaunchPlatformName(item))
                      .join('/') || '--'
                  );
                },
              },
            }),
          },
          {
            title: '联动上架平台',
            dataIndex: 'linked_platform',
            width: 280,
            required: !view,
            // 可编辑逻辑镜像「联动所属区域」列：select 多选 + 必填数组校验 + options 取产品上架平台 + 未选产品时 disabled
            formItem: (
              _text: any,
              record: IPCooperationProductItem,
              _index: number
            ) => ({
              view,
              componentClass: 'select',
              rules: [
                {
                  validator: (_rule: any, value: any) => {
                    // 联动上架平台必填：多选 value 为数组，空值可能是 undefined 或 []，
                    // 默认 required 规则对 [] 不生效（async-validator 无 type 时 [] 判非空），
                    // 须显式校验数组非空（镜像「联动所属区域」数组校验范式）
                    if (!Array.isArray(value) || !value.length) {
                      return Promise.reject(new Error('请选择联动上架平台'));
                    }
                    return Promise.resolve();
                  },
                },
              ],
              elementProps: {
                // options 名称经 launch_platform_options 映射（镜像「联动所属区域」列 country_region_options 映射范式）
                options: (record?.launch_platforms || []).map((r: string) => ({
                  name: getLaunchPlatformName(r),
                  value: r,
                })),
                className: 'w-[256px]',
                multiple: true,
                placeholder: '请先选择产品',
                showSearch: true,
                // 产品未选（无 id）时联动上架平台锁定，仅展示继承值不可改
                disabled: !record?.id,
                showComponent: (value: any) => {
                  return (
                    value
                      ?.map((item: any) => getLaunchPlatformName(item))
                      .join('/') || '--'
                  );
                },
              },
            }),
          },
          {
            title: '平台数据互通',
            dataIndex: 'data_interconnection',
            width: 120,
            // 平台数据互通改为可编辑下拉框（产品库固定枚举 0=不支持 1=支持）；view 态（详情只读）保留 Tag 展示
            formItem: (
              _text: any,
              record: IPCooperationProductItem,
              _index: number
            ) => ({
              view: view || lockProduct,
              componentClass: 'select',
              elementProps: {
                options: [
                  { name: '支持', value: DATA_INTERCONNECT_SUPPORTED },
                  { name: '不支持', value: DATA_INTERCONNECT_UNSUPPORTED },
                ],
                className: 'w-[120px]',
                placeholder: '请选择',
                // 产品未选（无 id）时锁定，仅展示带入值不可改（与联动列门控一致）
                disabled: !record?.id,
                showComponent: (value: any) =>
                  value === DATA_INTERCONNECT_SUPPORTED ? (
                    <Tag color="light-green" text="支持" size="small" />
                  ) : value === DATA_INTERCONNECT_UNSUPPORTED ? (
                    <Tag color="light-grey" text="不支持" size="small" />
                  ) : (
                    <span className="text-color-3">--</span>
                  ),
              },
            }),
          },
        ];
        // 只读（新建限 1 产品）/ 详情只读 均不追加「操作」列
        // if (!readonly && !view) {
        //   columns.push({
        //     title: '操作',
        //     dataIndex: 'operate',
        //     width: 60,
        //     fixed: 'right' as const,
        //     render: (_t: any, _r: IPCooperationProductItem, index: number) => (
        //       <Button type="link" size="small" danger onClick={() => remove(index)}>
        //         删除
        //       </Button>
        //     ),
        //   });
        // }
        return columns;
      };
    }, [
      productOptions,
      readonly,
      view,
      handleProductSelect,
      syncLinkedAfterProductFieldChange,
      getRegionName,
      getLaunchPlatformName,
      // 39696：两列 options 经 mergeRowValueOptions 读 Apollo 异步数据，缺依赖会闭包过期 → 下拉「暂无选项」（实测根因）
      countryRegionOptions,
      launchPlatformOptions,
    ]);

    const handleAdd = useMemoizedFn(() => {
      tableRef.current?.add({});
    });

    return (
      <div>
        <ProTableForm<IPCooperationProductItem>
          ref={tableRef}
          value={value}
          onChange={onChange}
          sortable={false}
          size="small"
          scroll={{ x: 'max-content' }}
          formColumns={formColumns as any}
          // summary={
          //   readonly || view
          //     ? undefined
          //     : () => (
          //         <Table.Summary>
          //           <Table.Summary.Row style={{ background: '#fff' }}>
          //             <Table.Summary.Cell index={0} colSpan={7}>
          //               <Button type="checked" icon={<Plus />} onClick={handleAdd} className="mb-2">
          //                 新增产品
          //               </Button>
          //             </Table.Summary.Cell>
          //           </Table.Summary.Row>
          //         </Table.Summary>
          //       )
          // }
        />
      </div>
    );
  }
);

ProductInfoTable.displayName = 'ProductInfoTable';

export default ProductInfoTable;
